/**
 * Type definitions and centralized constants for tenant applications.
 * All status string literals, labels, and badge colors live here
 * so every consumer stays in sync.
 */

// ---------------------------------------------------------------------------
// Application Status
// ---------------------------------------------------------------------------

export const APPLICATION_STATUSES = [
	'incomplete',
	'completed',
	'accepted',
	'declined',
	'withdrawn',
	'abandoned',
] as const

export type ApplicationStatus = (typeof APPLICATION_STATUSES)[number]

export const APPLICATION_STATUS_LABELS: Record<ApplicationStatus, string> = {
	incomplete: 'Incomplete',
	completed: 'Completed',
	accepted: 'Accepted',
	declined: 'Declined',
	withdrawn: 'Withdrawn',
	abandoned: 'Abandoned',
}

export const APPLICATION_STATUS_BADGE_COLORS: Record<
	ApplicationStatus,
	string
> = {
	incomplete: 'bg-amber-100 text-amber-800 border-amber-200',
	completed: 'bg-blue-100 text-blue-800 border-blue-200',
	accepted: 'bg-green-100 text-green-800 border-green-200',
	declined: 'bg-red-100 text-red-800 border-red-200',
	withdrawn: 'bg-gray-100 text-gray-800 border-gray-200',
	abandoned: 'bg-purple-100 text-purple-800 border-purple-200',
}

export const APPLICATION_STATUS_OPTIONS = APPLICATION_STATUSES.map((s) => ({
	label: APPLICATION_STATUS_LABELS[s],
	value: s,
}))

/**
 * Statuses the applicant-facing pages can display beyond the canonical list
 * statuses: while the screening pipeline runs they show the live pipeline
 * state ('processing' / 'failed') instead of collapsing to 'incomplete'.
 */
export type ApplicationDisplayStatus =
	| ApplicationStatus
	| 'processing'
	| 'failed'
	// The applicant has submitted and it's with the landlord (screening queued/
	// running, report generated awaiting a decision, or billing-held). Distinct
	// from 'incomplete' (a half-filled form the applicant still owns).
	| 'under_review'

/**
 * Resolve the status pill for applicant-facing pages (My Applications
 * listing, dashboard Recent Applications, application detail). Admin
 * decisions win, then the AI report's live pipeline state, then the
 * application row's own status. The listing and detail pages MUST both go
 * through this resolver — they used to derive status independently and
 * showed different pills for the same application.
 */
export function resolveApplicationDisplayStatus(
	appStatus: string,
	aiStatus?: string | null,
	opts?: { billingHold?: boolean },
): ApplicationDisplayStatus {
	if (appStatus === 'accepted') return 'accepted'
	if (appStatus === 'declined') return 'declined'
	if (appStatus === 'withdrawn') return 'withdrawn'
	if (appStatus === 'abandoned') return 'abandoned'
	if (aiStatus === 'completed') return 'completed'
	if (aiStatus === 'processing') return 'processing'
	if (aiStatus === 'failed') return 'failed'
	if (appStatus === 'completed') return 'completed'
	if (appStatus === 'processing') return 'processing'
	if (appStatus === 'failed') return 'failed'
	// Submitted (Flinks stamps 'submitted') or billing-held (report on hold on
	// the landlord's low balance, TT-12) — the applicant is done, so this is
	// "Under Review", never "Incomplete".
	if (appStatus === 'submitted' || opts?.billingHold) return 'under_review'
	return 'incomplete'
}

/**
 * Derive a household's overall status from its members. A property
 * application is a group (primary + co-applicants + optional guarantor +
 * optional children), and the admin cannot act on the household until every
 * adult is done with screening — so "worst-of-all" during screening, then
 * the primary's final status once everyone is done.
 *
 *  - Children are ignored (no screening step for them).
 *  - Guarantors DO count — if one was invited they have to finish too.
 *  - Any adult still `incomplete` → household is incomplete, regardless of
 *    what the primary shows.
 *  - Once no adult is incomplete, the primary's status is authoritative.
 *    Accept / decline / withdraw / abandon flip every member at once, so
 *    the primary reflects the household decision.
 *  - Falls back to the first adult if there's no primary at all (rare,
 *    but keeps the listing stable rather than crashing).
 */
export function deriveGroupListStatus(
	members: { listStatus: ApplicationStatus; applicationType: string }[],
): ApplicationStatus {
	const adults = members.filter((m) => m.applicationType !== 'child')
	// Decisions v3: once every APPLICANT is decided, the household reads as
	// the resolved decision — the primary is no longer authoritative (mixed
	// households are real; any accepted member outranks the primary's state).
	const resolved = resolveHouseholdDecision(
		adults
			.filter((m) => m.applicationType !== 'guarantor')
			.map((m) => m.listStatus),
	)
	if (resolved) return resolved
	if (adults.some((m) => m.listStatus === 'incomplete')) return 'incomplete'
	// LIVE household (some applicant undecided): decided members are settled
	// and must not distort the label (a declined primary next to an active
	// co-applicant is NOT a declined household — v3). Past the incomplete
	// guard every live member is 'completed', so the household reads
	// completed; the primary fallback only remains for degenerate rows.
	const decided = new Set(['accepted', 'declined', 'withdrawn', 'abandoned'])
	if (adults.some((m) => !decided.has(m.listStatus))) return 'completed'
	const primary = adults.find((m) => m.applicationType === 'primary')
	return primary?.listStatus ?? adults[0]?.listStatus ?? 'incomplete'
}

/**
 * Decisions v3 household resolution (DECISIONS-V3-PLAN.md §2), applicants
 * only (primary + co-applicants — guarantors carry their own decision and
 * never drive the household label):
 *   - ANY applicant still undecided → null (household is ACTIVE; the
 *     activity/list derivations pick the bucket as before).
 *   - ALL applicants decided → any accepted wins (a tenancy exists), else
 *     declined > withdrawn > abandoned.
 * Mirrored in SQL (landlord-dashboard-v2 CTEs) — change BOTH together.
 */
export function resolveHouseholdDecision(
	applicantStatuses: string[],
): 'accepted' | 'declined' | 'withdrawn' | 'abandoned' | null {
	if (applicantStatuses.length === 0) return null
	const decided = new Set(['accepted', 'declined', 'withdrawn', 'abandoned'])
	if (!applicantStatuses.every((status) => decided.has(status))) return null
	for (const status of ['accepted', 'declined', 'withdrawn'] as const) {
		if (applicantStatuses.includes(status)) return status
	}
	return 'abandoned'
}

/**
 * Statuses that indicate a final decision has been made.
 * Useful for conditionally showing Accept/Decline vs Change Status UI.
 */
export const DECIDED_STATUSES: ApplicationStatus[] = [
	'accepted',
	'declined',
	'withdrawn',
	'abandoned',
]

// ---------------------------------------------------------------------------
// Verification Status
// ---------------------------------------------------------------------------

export const VERIFICATION_STATUSES = [
	'pending',
	'in_progress',
	'verified',
	'rejected',
] as const

export type VerificationStatus = (typeof VERIFICATION_STATUSES)[number]

// ---------------------------------------------------------------------------
// Applicant Pill Status (visual indicator on applicant pills)
// ---------------------------------------------------------------------------

export const PILL_STATUSES = ['verified', 'pending', 'rejected'] as const

export type PillStatus = (typeof PILL_STATUSES)[number]

export const PILL_STATUS_ICON_COLORS: Record<PillStatus, string> = {
	verified: 'bg-green-500',
	pending: 'bg-amber-500',
	rejected: 'bg-red-500',
}

// ---------------------------------------------------------------------------
// AI Report / Processing Status
// ---------------------------------------------------------------------------

export const REPORT_STATUSES = [
	'pending',
	'processing',
	'completed',
	'failed',
] as const

export type ReportStatus = (typeof REPORT_STATUSES)[number]

export const REPORT_STATUS_OPTIONS = REPORT_STATUSES.map((s) => ({
	label: s.charAt(0).toUpperCase() + s.slice(1),
	value: s,
}))

// ---------------------------------------------------------------------------
// Step Status (uppercase display values used in superadmin step badges)
// ---------------------------------------------------------------------------

export const STEP_DISPLAY_STATUSES = [
	'PENDING',
	'COMPLETED',
	'FAILED',
	'NOT_STARTED',
] as const

export type StepDisplayStatus = (typeof STEP_DISPLAY_STATUSES)[number]

export const STEP_STATUS_BADGE_COLORS: Record<StepDisplayStatus, string> = {
	COMPLETED: 'bg-green-100 text-green-800 border-green-200',
	FAILED: 'bg-red-100 text-red-800 border-red-200',
	PENDING: 'bg-yellow-100 text-yellow-800 border-yellow-200',
	NOT_STARTED: 'bg-gray-100 text-gray-800 border-gray-200',
}

// ---------------------------------------------------------------------------
// Guarantor Status
// ---------------------------------------------------------------------------

export type GuarantorStatus = 'invited' | 'screening' | 'completed' | 'declined'

export interface Property {
	id: string
	name: string
	address: string
	city: string
	province: string
	postalCode: string
	images: string[]
	propertyType: 'single-family' | 'multi-family'
	monthlyRent: number
	amenities: string[]
	bedrooms?: number
}

export interface PropertyUnit {
	id: string
	type: string
	bedrooms?: number
	monthlyRent: number
}

export interface Guarantor {
	id: string
	email: string
	name?: string
	status: GuarantorStatus
	invitedAt: string
	completedAt?: string
}

export interface TenantApplication {
	id: string
	propertyId: string
	property: Property
	unitId?: string
	unit?: PropertyUnit
	applicationStatus: ApplicationDisplayStatus
	verificationStatus: VerificationStatus
	trueTenantScore?: number
	applicationDate: string
	submittedAt?: string
	moveInDate?: string
	guarantorRequired: boolean
	guarantors: Guarantor[]
	groupId?: string
}

export interface ScoreBreakdown {
	overall: number
	creditScore: number
	employmentVerification: number
	rentalHistory: number
	documentVerification: number
}

export interface VerificationStep {
	id: string
	name: string
	status: 'completed' | 'in_progress' | 'pending'
	completedAt?: string
}
